'use client';

import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { ExternalLink } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/lib/i18n';

const officialLinks = [
  { label: 'service-public.fr', href: 'https://www.service-public.fr', noteKey: 'Démarches officielles' },
  { label: 'ANEF', href: 'https://administration-etrangers-en-france.interieur.gouv.fr', noteKey: 'Étrangers en France' },
  { label: 'OFII', href: 'https://www.ofii.fr', noteKey: 'Intégration' },
  { label: 'immigration.interieur.gouv.fr', href: 'https://www.immigration.interieur.gouv.fr', noteKey: 'Immigration' },
  { label: 'CAF', href: 'https://www.caf.fr', noteKey: 'Aides familiales' },
  { label: 'ameli.fr', href: 'https://www.ameli.fr', noteKey: 'Assurance maladie' },
];

const officialLinksAr = [
  { label: 'service-public.fr', href: 'https://www.service-public.fr', noteKey: 'الإجراءات الرسمية' },
  { label: 'ANEF', href: 'https://administration-etrangers-en-france.interieur.gouv.fr', noteKey: 'الأجانب في فرنسا' },
  { label: 'OFII', href: 'https://www.ofii.fr', noteKey: 'الاندماج' },
  { label: 'immigration.interieur.gouv.fr', href: 'https://www.immigration.interieur.gouv.fr', noteKey: 'الهجرة' },
  { label: 'CAF', href: 'https://www.caf.fr', noteKey: 'المساعدات العائلية' },
  { label: 'ameli.fr', href: 'https://www.ameli.fr', noteKey: 'التأمين الصحي' },
];

export default function FooterSection() {
  const { user, loading } = useAuth();
  const { t, isRTL } = useLanguage();
  const isAuthenticated = !loading && !!user;

  const links = isRTL ? officialLinksAr : officialLinks;

  const navLinks = [
    { label: t?.footer?.links?.home, href: '/home-prompts-screen' },
    { label: t?.footer?.links?.freeGuides, href: '/home-prompts-screen#prompts' },
    { label: t?.footer?.links?.aiAssistant, href: '/ai-chat-interface' },
    ...(!isAuthenticated ? [
      { label: t?.footer?.links?.login, href: '/sign-up-login-screen' },
      { label: t?.footer?.links?.register, href: '/sign-up-login-screen' },
    ] : []),
  ];

  return (
    <footer className="bg-gray-900 text-gray-400" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <AppLogo src="/assets/images/dalili-1775932023691.png" size={32} />
              <span className="font-jakarta font-700 text-lg text-white">Dalili</span>
            </div>
            <p className="text-sm leading-relaxed text-gray-500 max-w-xs">{t?.footer?.description}</p>
            <div className="mt-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-xs text-gray-500">{t?.footer?.upToDate}</span>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-jakarta text-sm font-600 text-white mb-4 uppercase tracking-wider">
              {t?.footer?.navigation}
            </h4>
            <ul className="space-y-2">
              {navLinks?.map((link) => (
                <li key={`footer-nav-${link?.label}`}>
                  <Link href={link?.href} className="text-sm text-gray-500 hover:text-white transition-colors">
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Official sources */}
          <div>
            <h4 className="font-jakarta text-sm font-600 text-white mb-4 uppercase tracking-wider">
              {t?.footer?.officialSources}
            </h4>
            <ul className="space-y-2">
              {links?.map((link) => (
                <li key={`footer-src-${link?.label}`}>
                  <a
                    href={link?.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-white transition-colors group"
                  >
                    <ExternalLink size={12} className="text-gray-600 group-hover:text-accent-400 transition-colors" />
                    <span>{link?.label}</span>
                    <span className="text-xs text-gray-600">— {link?.noteKey}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-gray-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-gray-600">{t?.footer?.copyright}</p>
          <div className="flex items-center gap-4 text-xs text-gray-600">
            <span> CONTACT US: contact@aigle-asso.org </span>
          </div>
        </div>
      </div>
    </footer>
  );
}